import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  createListing,
  createOrder,
  attachPaymentUrl,
  fetchBoard,
  fetchListing,
  fetchOrder,
  registerClick,
  setAutoBid,
  boardVersion,
  fetchSubmissions,
  upsertAppUser,
  MIN_BID,
} from "./db.server";
import { createInvoice } from "./duitku.server";

export const getBoard = createServerFn({ method: "GET" }).handler(async () => fetchBoard());

export const getListing = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => fetchListing(data.slug));

export const submitListing = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        name: z.string().min(2).max(120),
        url: z.string().min(3).max(300),
        description: z.string().max(400).default(""),
        category: z.string().min(2).max(60),
        ownerEmail: z.string().email().optional(),
        idToken: z.string().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { idToken, ...listing } = data;
    const { authConfigured, verifyIdToken } = await import("./admin-auth.server");
    if (authConfigured()) {
      const email = idToken ? await verifyIdToken(idToken) : null;
      if (!email) throw new Error("Silakan masuk dengan Google terlebih dahulu untuk menyebar listing.");
      return createListing({ ...listing, ownerEmail: email });
    }
    return createListing(listing);
  });

export const getBoardVersion = createServerFn({ method: "GET" }).handler(async () => ({
  version: await boardVersion(),
}));

export const listSubmissions = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        email: z.string().email().optional(),
        slugs: z.array(z.string().min(1)).max(100).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => ({ items: await fetchSubmissions(data) }));

export const syncProfile = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        subject: z.string().min(1).max(200),
        email: z.string().email().optional(),
        name: z.string().max(200).optional(),
        picture: z.string().max(600).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => upsertAppUser(data));

export const trackClick = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const { getRequest } = await import("@tanstack/react-start/server");
    let fingerprint = "anonim";
    try {
      const req = getRequest();
      const ip =
        req.headers.get("cf-connecting-ip") ??
        req.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
        "unknown";
      const ua = req.headers.get("user-agent") ?? "unknown";
      const bytes = new TextEncoder().encode(`${ip}|${ua}`);
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      fingerprint = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
      /* di luar konteks request */
    }
    const result = await registerClick(data.slug, fingerprint);
    return { url: result?.url ?? null, counted: result?.counted ?? false };
  });


export const saveAutoBid = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ slug: z.string().min(1), token: z.string().uuid(), max: z.number().int().min(0).max(100_000_000) }).parse(d),
  )
  .handler(async ({ data }) => setAutoBid(data.slug, data.token, data.max));

export const startPayment = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        amount: z.number().int().min(MIN_BID).max(100_000_000),
        kind: z.enum(["bid", "package"]),
        packageId: z.string().optional(),
        origin: z.string().url(),
        email: z.string().email().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { merchantOrderId, listingName } = await createOrder({
      slug: data.slug,
      amount: data.amount,
      kind: data.kind,
      packageId: data.packageId,
    });
    const invoice = await createInvoice({
      merchantOrderId,
      amount: data.amount,
      productDetails:
        data.kind === "bid" ? `Naik peringkat ${listingName}` : `Paket ${data.packageId} untuk ${listingName}`,
      origin: data.origin,
      email: data.email,
    });
    await attachPaymentUrl(merchantOrderId, invoice.paymentUrl, invoice.reference);
    return { merchantOrderId, paymentUrl: invoice.paymentUrl };
  });

export const getOrder = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ merchantOrderId: z.string().min(3) }).parse(d))
  .handler(async ({ data }) => fetchOrder(data.merchantOrderId));

/** Peringkat hari ini & minggu ini untuk section beranda. */
export const getRankings = createServerFn({ method: "GET" }).handler(async () => {
  const { fetchRankings } = await import("./db.server");
  return fetchRankings();
});
